// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, memo, useEffect, useMemo } from 'react'

// others
import { QUERY_PARAMS } from '../../constants'
import { LegalTeamUserFilterProps, LegalTeamUserOption } from '../../interfaceTypes/props'

const ALL_OPTION: LegalTeamUserOption = { id: QUERY_PARAMS.ALL, name: 'All' }

/**
 * "Legal Person" filter for a single matter: offers only the members of that matter's legal team
 * (passed in, no fetching), with All as the default. Unlike <UserFilter>, it never searches the
 * firm's full user list.
 */
const LegalTeamUserFilter: FC<LegalTeamUserFilterProps> = ({ label, users, setParams, getFilterValues }) => {
  const options = useMemo(() => [ALL_OPTION, ...users], [users])
  const selectedId = getFilterValues(QUERY_PARAMS.USER)
  const selected = users.find(user => user.id === selectedId)

  // A link can carry a person who isn't on this matter's team; fall back to All rather than
  // filtering by someone the dropdown can't show.
  useEffect(() => {
    if (selectedId !== QUERY_PARAMS.ALL && !selected) setParams(QUERY_PARAMS.USER, QUERY_PARAMS.ALL)
  }, [selectedId, selected, setParams])

  return (
    <Autocomplete
      options={options}
      value={selected || ALL_OPTION}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, option) => setParams(QUERY_PARAMS.USER, option?.id || QUERY_PARAMS.ALL)}
      renderInput={params => <TextField {...params} label={label} fullWidth />}
      ListboxProps={{ style: { maxHeight: '190px' } }}
      noOptionsText="No legal team members"
      sx={{ width: '100%' }}
      selectOnFocus
      handleHomeEndKeys
    />
  )
}

export default memo(LegalTeamUserFilter)
